'use client';

import { useState, useMemo } from 'react';
import type { Metadata } from 'next'
import Breadcrumb from '@/components/Breadcrumb'
import { useCountries } from '@/hooks/useCountries'
import CountriesStats from '@/components/CountriesStats'
import CountryCard from '@/components/CountryCard'
import QuickCompareTool from '@/components/QuickCompareTool'
import GlobalSearch from '@/components/GlobalSearch'
import { Search, Filter, SortAsc, SortDesc, Grid, List } from 'lucide-react'

export default function CountriesPage() {
  const { countries, stats, loading, error, searchCountries, filterByContinent, sortCountries } = useCountries();
  
  // 状态管理
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('all');
  const [sortBy, setSortBy] = useState<'area' | 'name'>('area');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  // 获取所有大洲
  const continents = useMemo(() => {
    if (!stats) return [];
    return Object.keys(stats.continents).sort();
  }, [stats]);

  // 过滤和排序后的国家列表
  const filteredCountries = useMemo(() => {
    let result = countries;

    // 搜索过滤
    if (searchQuery.trim()) {
      result = searchCountries(searchQuery);
    }

    // 大洲过滤
    if (selectedContinent !== 'all') {
      result = filterByContinent(selectedContinent);
    }

    // 对过滤后的结果进行排序
    const sorted = [...result];
    switch (sortBy) {
      case 'area':
        sorted.sort((a, b) => sortOrder === 'desc' ? b.area_km2 - a.area_km2 : a.area_km2 - b.area_km2);
        break;
      case 'name':
        sorted.sort((a, b) => sortOrder === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
        break;
    }

    return sorted;
  }, [countries, searchQuery, selectedContinent, sortBy, sortOrder, searchCountries, filterByContinent]);

  // 处理比较
  const handleCompare = (country: any) => {
    const url = `/map?a=${country.slug}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error Loading Countries</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8">
      <Breadcrumb items={[
        { label: 'Browse', href: '/browse' },
        { label: 'Countries' }
      ]} />
      
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">The True Size of Countries - An Interactive Comparison Map</h1>
        <p className="text-slate-600 text-lg">
          Discover the true size of countries around the world. Compare and explore with our interactive map tool.
        </p>
      </div>


      {/* 快速比较工具 */}
      <div className="mb-8">
        <QuickCompareTool countries={countries} />
      </div>

      {/* 搜索和筛选 */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* 搜索框 */}
          <div className="w-full lg:w-80">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search countries..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* 大洲筛选 */}
          <div className="flex gap-2 flex-1">
            <select
              value={selectedContinent}
              onChange={(e) => setSelectedContinent(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            >
              <option value="all">All Continents</option>
              {continents.map(continent => (
                <option key={continent} value={continent}>
                  {continent} ({stats?.continents[continent] || 0})
                </option>
              ))}
            </select>

            {/* 排序 */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
            >
              <option value="area-desc">Largest Area</option>
              <option value="area-asc">Smallest Area</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
            </select>

            {/* 视图模式 */}
            <div className="flex border border-slate-200 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-sky-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                aria-label="Switch to grid view"
                aria-pressed={viewMode === 'grid'}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-sky-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                aria-label="Switch to list view"
                aria-pressed={viewMode === 'list'}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 结果统计 */}
      <div className="mb-6">
        <p className="text-slate-600">
          Showing {filteredCountries.length} of {countries.length} countries
        </p>
      </div>

      {/* 国家列表 */}
      <div className={`grid gap-3 ${
        viewMode === 'grid' 
          ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6' 
          : 'grid-cols-1'
      }`}>
        {filteredCountries.map((country) => (
          <CountryCard
            key={country.iso3}
            country={country}
            onCompare={handleCompare}
            showRank={sortBy === 'area'}
          />
        ))}
      </div>

      {/* 空状态 */}
      {filteredCountries.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400 mb-4">
            <Search className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No countries found</h3>
          <p className="text-slate-500">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}

      {/* 关于国家真实大小的描述 */}
      <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Understanding True Size of Countries</h2>
        <p className="text-slate-700 leading-relaxed">
          Traditional world maps often distort the true size of countries due to map projections, particularly affecting countries near the poles. Our interactive platform reveals the genuine geographical dimensions of nations worldwide, helping you discover surprising scale relationships. For instance, Russia appears much larger than it actually is on Mercator projections, while countries like Greenland seem disproportionately huge. Explore these fascinating geographical realities through our precise scale comparison tools and discover the true relative sizes of countries around the globe.
        </p>
      </div>

      {/* 相关页面导航 */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">More Comparisons</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <a 
            href="/true-size-of-continents"
            className="inline-flex items-center justify-center px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 hover:text-purple-800 rounded-lg font-medium transition-colors border border-purple-200 hover:border-purple-300 text-sm"
          >
            True Size of Continents
          </a>
          <a 
            href="/true-size-of-cities"
            className="inline-flex items-center justify-center px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 hover:text-indigo-800 rounded-lg font-medium transition-colors border border-indigo-200 hover:border-indigo-300 text-sm"
          >
            True Size of Cities
          </a>
          <a 
            href="/true-size-of-oceans"
            className="inline-flex items-center justify-center px-3 py-2 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 hover:text-cyan-800 rounded-lg font-medium transition-colors border border-cyan-200 hover:border-cyan-300 text-sm"
          >
            True Size of Oceans
          </a>
          <a 
            href="/true-size-of-lakes"
            className="inline-flex items-center justify-center px-3 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 hover:text-teal-800 rounded-lg font-medium transition-colors border border-teal-200 hover:border-teal-300 text-sm"
          >
            True Size of Lakes
          </a>
          <a 
            href="/true-size-of-islands"
            className="inline-flex items-center justify-center px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 hover:text-orange-800 rounded-lg font-medium transition-colors border border-orange-200 hover:border-orange-300 text-sm"
          >
            True Size of Islands
          </a>
        </div>
      </div>
    </div>
  )
}
